import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { getAllPosts } from "@/lib/blog";
import { useMemo } from "react";

// Define separate featured posts for each language since content is not 1:1 translated
const EN_SLUGS = [
    "the-professionalism-trap-why-growing-up-shouldnt-mean-selling-out",
    "titanic-culture-1",
    "one-size-fits-all-organizational-design",
    "revisiting-team-building-in-2025"
];

const DE_SLUGS = [
    "angewandte-organisationsentwicklung-teil-3-kmu",
    "authenticity-at-work-engagement-crisis",
    "ueberraschung-erfolgreichsten-ceos-operative-tiefe",
    "mission-accomplished-13-monate-training-dunk"
];

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
};

const FeaturedPosts = () => {
    const { language, t } = useLanguage();
    const posts = getAllPosts(language);

    const featuredPosts = useMemo(() => {
        const featuredSlugs = language === 'de' ? DE_SLUGS : EN_SLUGS;

        // Filter posts to get only the featured ones
        return posts
            .filter(post => featuredSlugs.includes(post.slug))
            // Sort to match the order in featuredSlugs
            .sort((a, b) => {
                return featuredSlugs.indexOf(a.slug) - featuredSlugs.indexOf(b.slug);
            });
    }, [posts, language]);

    const dateFormatter = useMemo(() => new Intl.DateTimeFormat(language === 'de' ? 'de-DE' : 'en-US', DATE_OPTIONS), [language]);

    if (featuredPosts.length === 0) return null;

    return (
        <section className="py-20 px-0 sm:px-4 bg-secondary/30">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center mb-12 space-y-4">
                    <h2 className="text-3xl md:text-4xl font-bold">
                        {t("featured.title")}
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        {t("featured.description")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                    {featuredPosts.map((post) => (
                        <div
                            key={post.slug}
                            className="group relative flex flex-col bg-background rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-border/50 h-full"
                        >
                            <div className="p-6 md:p-8 flex flex-col flex-grow">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                                    <Calendar className="w-4 h-4" />
                                    <time dateTime={post.date} suppressHydrationWarning>
                                        {dateFormatter.format(new Date(post.date))}
                                    </time>
                                    {post.author && (
                                        <>
                                            <span className="mx-1">•</span>
                                            <span>{post.author}</span>
                                        </>
                                    )}
                                </div>

                                <h3 className="text-xl md:text-2xl font-bold mb-4 group-hover:text-primary transition-colors line-clamp-2">
                                    <Link
                                        to={`/blog/${post.slug}`}
                                        title={post.title}
                                        className="after:absolute after:inset-0 outline-none focus:ring-2 focus:ring-primary rounded-2xl"
                                    >
                                        {post.title}
                                    </Link>
                                </h3>

                                <p className="text-muted-foreground mb-6 line-clamp-3 flex-grow leading-relaxed">
                                    {post.excerpt}
                                </p>

                                <div className="flex items-center text-primary font-medium mt-auto group-hover:translate-x-1 transition-transform">
                                    {t("featured.readMore")}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-12">
                    <Link
                        to="/blog"
                        title={t("featured.viewAll")}
                        className="inline-flex items-center justify-center px-8 py-3 rounded-full border-2 border-primary text-primary font-medium hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                    >
                        {t("featured.viewAll")}
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default FeaturedPosts;
